import {
    formatDate,
    formatNumber,
    formatNumberForDimensions,
    getDimensionUnit,
    getTradeUnit,
    ListingBuyerModel,
    QuoteEntry,
    RFQBuyerModel,
    ThicknessOption,
    TranslateFunc,
} from '@steelbuy/ts-shared';
import { buildProductName } from '../listings-table/listingsTableUtil';

const TO_SHOW_LISTINGS_NUMBER = 2;

export const myRfqsTableHeading = ['rfqReferenceNo', 'createdAt', 'materialSpecs', 'totalWeight', 'rfqStatus'];

type QuoteTableConfig = Record<
    string,
    {
        getHeading: (t: TranslateFunc) => string;
        mapValue?: (quote: RFQBuyerModel, t: TranslateFunc) => string | string[] | number | undefined;
    }
>;

const formatDimensionLabel = (value: number, t: TranslateFunc): string =>
    t(`domainModel:material.dimensions.units.${getDimensionUnit()}.labelWithValue`, {
        value: formatNumberForDimensions(value),
    });

const buildRangeDimensionsText = (entry: QuoteEntry, t: TranslateFunc): string => {
    const { thicknessMin, thicknessMax, materialDimensions } = entry;

    const thicknessText =
        thicknessMin !== undefined && thicknessMax !== undefined
            ? t(`domainModel:material.dimensions.units.${getDimensionUnit()}.rangeLabel`, {
                  min: formatNumberForDimensions(thicknessMin),
                  max: formatNumberForDimensions(thicknessMax),
              })
            : '';

    const parts = [thicknessText, formatDimensionLabel(materialDimensions.width, t)];

    if ('length' in materialDimensions && materialDimensions.length) {
        parts.push(formatDimensionLabel(materialDimensions.length, t));
    }

    return parts.join(' x ');
};

const isRangeEntry = (entry: QuoteEntry): boolean =>
    entry.thicknessOption === ThicknessOption.RANGE ||
    (entry.thicknessMin !== undefined && entry.thicknessMax !== undefined);

const getEntryProductName = (entry: QuoteEntry, t: TranslateFunc): string => {
    if (!isRangeEntry(entry)) {
        return buildProductName(entry as ListingBuyerModel, t);
    }

    return buildProductName(entry as ListingBuyerModel, t, {
        dimensions: (listing, translate) => buildRangeDimensionsText(listing, translate),
    });
};

const getMaterialSpecifications = (quote: RFQBuyerModel, t: TranslateFunc): string[] => {
    const entries = quote.quoteEntry;

    const visibleEntries = entries.slice(0, TO_SHOW_LISTINGS_NUMBER).map((entry) => getEntryProductName(entry, t));

    if (entries.length > TO_SHOW_LISTINGS_NUMBER) {
        visibleEntries.push(
            t('application.myQuotes.extraListingsCount', {
                count: entries.length - TO_SHOW_LISTINGS_NUMBER,
            })
        );
    }

    return visibleEntries;
};

export const quoteTableConfig: QuoteTableConfig = {
    rfqReferenceNo: {
        getHeading: (t) => t('application.myQuotes.tableHeadings.rfqReferenceNo'),
        mapValue: (quote) => quote.quoteReferenceNumber,
    },
    createdAt: {
        getHeading: (t) => t('application.myQuotes.tableHeadings.createdAt'),
        mapValue: (quote) => formatDate(new Date(quote?.createdAt.value)),
    },
    materialSpecs: {
        getHeading: (t) => t('application.myQuotes.tableHeadings.materialSpecs'),
        mapValue: getMaterialSpecifications,
    },
    totalWeight: {
        getHeading: (t) => t('application.myQuotes.tableHeadings.totalWeight'),
        mapValue: (quote, t) =>
            t(
                `domainModel:material.tradeUnit.value.${getTradeUnit({
                    materialType: quote.quoteEntry[0].material.materialType,
                })}.quantity`,
                {
                    count: quote.totalWeight,
                    formatted: formatNumber(quote.totalWeight),
                }
            ),
    },
    rfqStatus: {
        getHeading: (t) => t('application.myQuotes.tableHeadings.rfqStatus'),
        mapValue: (quote, t) => t(`application.myQuotes.rfqStatus.${quote.status.value}`),
    },
};
